import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { handleApiError, isPermissionError } from "@/lib/apiErrorHandler";

export type OrgRole = "owner" | "admin" | "dispatcher" | "finance" | "readonly";

export interface Organization {
  id: string;
  name: string;
  slug: string;
  logo_url: string | null;
  billing_email: string | null;
  phone: string | null;
  address: string | null;
  city: string | null;
  postal_code: string | null;
  country: string;
  mfa_required_roles: OrgRole[];
  settings: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface OrganizationMember {
  id: string;
  organization_id: string;
  user_id: string;
  role: OrgRole;
  is_active: boolean;
  invited_by: string | null;
  profiles?: {
    full_name: string | null;
    avatar_url: string | null;
  } | null;
  joined_at: string;
  created_at: string;
  updated_at: string;
  profile?: {
    full_name: string | null;
    avatar_url: string | null;
    email?: string;
  };
}

export interface OrganizationInvite {
  id: string;
  organization_id: string;
  email: string;
  role: OrgRole;
  token: string;
  invited_by: string;
  expires_at: string;
  accepted_at: string | null;
  revoked_at: string | null;
  created_at: string;
}

interface OrganizationContextType {
  organizations: Organization[];
  currentOrganization: Organization | null;
  currentRole: OrgRole | null;
  members: OrganizationMember[];
  invites: OrganizationInvite[];
  loading: boolean;
  error: string | null;
  switchOrganization: (orgId: string) => void;
  createOrganization: (data: CreateOrgData) => Promise<Organization | null>;
  updateOrganization: (orgId: string, data: Partial<Organization>) => Promise<boolean>;
  inviteMember: (email: string, role: OrgRole) => Promise<boolean>;
  updateMemberRole: (memberId: string, role: OrgRole) => Promise<boolean>;
  removeMember: (memberId: string) => Promise<boolean>;
  revokeInvite: (inviteId: string) => Promise<boolean>;
  refreshOrganization: () => Promise<void>;
  hasPermission: (requiredRoles: OrgRole[]) => boolean;
  needsOrgSetup: boolean;
}

interface CreateOrgData {
  name: string;
  slug?: string;
  billing_email?: string;
  phone?: string;
  address?: string;
  city?: string;
  postal_code?: string;
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

const CURRENT_ORG_KEY = "bricos_current_org";

export const OrganizationProvider = ({ children }: { children: ReactNode }) => {
  const { user, loading: authLoading } = useAuth();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [currentOrganization, setCurrentOrganization] = useState<Organization | null>(null);
  const [currentRole, setCurrentRole] = useState<OrgRole | null>(null);
  const [members, setMembers] = useState<OrganizationMember[]>([]);
  const [invites, setInvites] = useState<OrganizationInvite[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [needsOrgSetup, setNeedsOrgSetup] = useState(false);

  // Fetch organizations for current user
  const fetchOrganizations = useCallback(async () => {
    if (!user) {
      setOrganizations([]);
      setCurrentOrganization(null);
      setCurrentRole(null);
      setLoading(false);
      return;
    }

    try {
      // Get memberships
      const { data: memberships, error: memberError } = await supabase
        .from("organization_members")
        .select(`
          organization_id,
          role,
          organizations (*)
        `)
        .eq("user_id", user.id)
        .eq("is_active", true);

      if (memberError) throw memberError;

      if (!memberships || memberships.length === 0) {
        setOrganizations([]);
        setCurrentOrganization(null);
        setCurrentRole(null);
        setNeedsOrgSetup(true);
        setLoading(false);
        return;
      }

      const orgs = memberships
        .map((m) => m.organizations as unknown as Organization)
        .filter(Boolean);
      
      setOrganizations(orgs);
      setNeedsOrgSetup(false);

      // Restore last selected org or use first one
      const savedOrgId = localStorage.getItem(CURRENT_ORG_KEY);
      const savedOrg = orgs.find((o) => o.id === savedOrgId);
      const selectedOrg = savedOrg || orgs[0];

      if (selectedOrg) {
        setCurrentOrganization(selectedOrg);
        const membership = memberships.find((m) => m.organization_id === selectedOrg.id);
        setCurrentRole(membership?.role as OrgRole || null);
        localStorage.setItem(CURRENT_ORG_KEY, selectedOrg.id);
      }
    } catch (err) {
      handleApiError(err, { context: "fetching organizations", severity: "silent" });
      setOrganizations([]);
      setCurrentOrganization(null);
      setCurrentRole(null);
      setError(isPermissionError(err) ? "Permission denied" : "Failed to load organizations");
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Fetch members and invites for current org
  const fetchMembersAndInvites = useCallback(async () => {
    if (!currentOrganization) {
      setMembers([]);
      setInvites([]);
      return;
    }

    try {
      // Fetch members - handle errors gracefully
      const { data: memberData, error: memberError } = await supabase
        .from("organization_members")
        .select("*")
        .eq("organization_id", currentOrganization.id)
        .eq("is_active", true);

      if (memberError) {
        handleApiError(memberError, { context: "fetching org members", severity: "silent" });
        setMembers([]);
        setInvites([]);
        return;
      }

      // Fetch profiles for members - graceful
      const memberUserIds = (memberData || []).map((m) => m.user_id);
      
      let profilesData: { user_id: string; full_name: string | null; avatar_url: string | null }[] = [];
      if (memberUserIds.length > 0) {
        const { data, error: profileError } = await supabase
          .from("profiles")
          .select("user_id, full_name, avatar_url")
          .in("user_id", memberUserIds);
        
        if (profileError) {
          handleApiError(profileError, { context: "fetching member profiles", severity: "silent" });
        } else {
          profilesData = data || [];
        }
      }

      // Transform to include profile data
      const profilesMap = new Map(
        profilesData.map((p) => [p.user_id, p])
      );
      const transformedMembers = (memberData || []).map((m) => ({
        ...m,
        profiles: profilesMap.get(m.user_id) || null,
        profile: profilesMap.get(m.user_id) || null,
      }));

      setMembers(transformedMembers);

      // Fetch pending invites (only for owner/admin) - always graceful
      if (currentRole === "owner" || currentRole === "admin") {
        try {
          const { data: inviteData, error: inviteError } = await supabase
            .from("organization_invites")
            .select("*")
            .eq("organization_id", currentOrganization.id)
            .is("accepted_at", null)
            .is("revoked_at", null)
            .gt("expires_at", new Date().toISOString());

          if (inviteError) {
            handleApiError(inviteError, { context: "fetching org invites", severity: "silent" });
            setInvites([]);
          } else {
            setInvites(inviteData || []);
          }
        } catch (inviteErr) {
          handleApiError(inviteErr, { context: "fetching org invites", severity: "silent" });
          setInvites([]);
        }
      } else {
        setInvites([]);
      }
    } catch (err) {
      handleApiError(err, { context: "fetching members and invites", severity: "silent" });
      setMembers([]);
      setInvites([]);
    }
  }, [currentOrganization, currentRole]);

  // Initial load
  useEffect(() => {
    if (!authLoading) {
      fetchOrganizations();
    }
  }, [authLoading, fetchOrganizations]);

  // Load members when org changes
  useEffect(() => {
    fetchMembersAndInvites();
  }, [fetchMembersAndInvites]);

  // Real-time subscriptions
  useEffect(() => {
    if (!currentOrganization) return;

    const memberChannel = supabase
      .channel(`org-members-${currentOrganization.id}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "organization_members",
          filter: `organization_id=eq.${currentOrganization.id}`,
        },
        () => {
          fetchMembersAndInvites();
        }
      )
      .subscribe();

    const inviteChannel = supabase
      .channel(`org-invites-${currentOrganization.id}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "organization_invites",
          filter: `organization_id=eq.${currentOrganization.id}`,
        },
        () => {
          fetchMembersAndInvites();
        }
      )
      .subscribe();

    return () => {
      memberChannel.unsubscribe();
      inviteChannel.unsubscribe();
    };
  }, [currentOrganization, fetchMembersAndInvites]);

  const switchOrganization = (orgId: string) => {
    const org = organizations.find((o) => o.id === orgId);
    if (org) {
      setCurrentOrganization(org);
      localStorage.setItem(CURRENT_ORG_KEY, orgId);
      
      // Get role for this org
      supabase
        .from("organization_members")
        .select("role")
        .eq("organization_id", orgId)
        .eq("user_id", user?.id)
        .single()
        .then(({ data }) => {
          setCurrentRole(data?.role as OrgRole || null);
        });
    }
  };

  const createOrganization = async (data: CreateOrgData): Promise<Organization | null> => {
    if (!user) return null;

    try {
      const slug = data.slug || data.name.toLowerCase().replace(/[^a-z0-9]+/g, "-");

      // Create organization
      const { data: org, error: orgError } = await supabase
        .from("organizations")
        .insert({
          name: data.name,
          slug,
          billing_email: data.billing_email || user.email,
          phone: data.phone,
          address: data.address,
          city: data.city,
          postal_code: data.postal_code,
        })
        .select()
        .single();

      if (orgError) throw orgError;

      // Add self as owner
      const { error: memberError } = await supabase
        .from("organization_members")
        .insert({
          organization_id: org.id,
          user_id: user.id,
          role: "owner",
        });

      if (memberError) throw memberError;

      // Log audit event
      await supabase.from("organization_audit_log").insert({
        organization_id: org.id,
        user_id: user.id,
        action: "create_organization",
        entity_type: "organization",
        entity_id: org.id,
        after_state: org,
      });

      toast.success("Organization created successfully!");
      await fetchOrganizations();
      return org as Organization;
    } catch (err: unknown) {
      console.error("Error creating organization:", err);
      const message = err instanceof Error ? err.message : "Failed to create organization";
      toast.error(message);
      return null;
    }
  };

  const updateOrganization = async (orgId: string, data: Partial<Organization>): Promise<boolean> => {
    try {
      // Extract only the fields that can be updated
      const updateData: Record<string, unknown> = {};
      if (data.name !== undefined) updateData.name = data.name;
      if (data.slug !== undefined) updateData.slug = data.slug;
      if (data.logo_url !== undefined) updateData.logo_url = data.logo_url;
      if (data.billing_email !== undefined) updateData.billing_email = data.billing_email;
      if (data.phone !== undefined) updateData.phone = data.phone;
      if (data.address !== undefined) updateData.address = data.address;
      if (data.city !== undefined) updateData.city = data.city;
      if (data.postal_code !== undefined) updateData.postal_code = data.postal_code;
      if (data.country !== undefined) updateData.country = data.country;

      const { error } = await supabase
        .from("organizations")
        .update(updateData)
        .eq("id", orgId);

      if (error) throw error;

      // Update local state
      setOrganizations((prev) =>
        prev.map((o) => (o.id === orgId ? { ...o, ...data } : o))
      );
      if (currentOrganization?.id === orgId) {
        setCurrentOrganization((prev) => (prev ? { ...prev, ...data } : null));
      }

      toast.success("Organization updated!");
      return true;
    } catch (err) {
      console.error("Error updating organization:", err);
      toast.error("Failed to update organization");
      return false;
    }
  };

  const inviteMember = async (email: string, role: OrgRole): Promise<boolean> => {
    if (!currentOrganization || !user) return false;

    try {
      const { error } = await supabase.from("organization_invites").insert({
        organization_id: currentOrganization.id,
        email,
        role,
        invited_by: user.id,
      });

      if (error) throw error;

      // Log audit event
      await supabase.from("organization_audit_log").insert({
        organization_id: currentOrganization.id,
        user_id: user.id,
        action: "invite_member",
        entity_type: "invite",
        after_state: { email, role },
      });

      toast.success(`Invitation sent to ${email}`);
      await fetchMembersAndInvites();
      return true;
    } catch (err: unknown) {
      console.error("Error inviting member:", err);
      const message = err instanceof Error ? err.message : "Failed to send invite";
      toast.error(message);
      return false;
    }
  };

  const updateMemberRole = async (memberId: string, role: OrgRole): Promise<boolean> => {
    if (!currentOrganization || !user) return false;

    try {
      const member = members.find((m) => m.id === memberId);
      if (!member) throw new Error("Member not found");

      // Prevent changing owner role
      if (member.role === "owner" && role !== "owner") {
        toast.error("Cannot change owner role. Transfer ownership first.");
        return false;
      }

      const { error } = await supabase
        .from("organization_members")
        .update({ role })
        .eq("id", memberId);

      if (error) throw error;

      // Log audit event
      await supabase.from("organization_audit_log").insert({
        organization_id: currentOrganization.id,
        user_id: user.id,
        action: "update_member_role",
        entity_type: "member",
        entity_id: memberId,
        before_state: { role: member.role },
        after_state: { role },
      });

      toast.success("Role updated successfully");
      await fetchMembersAndInvites();
      return true;
    } catch (err) {
      console.error("Error updating member role:", err);
      toast.error("Failed to update role");
      return false;
    }
  };

  const removeMember = async (memberId: string): Promise<boolean> => {
    if (!currentOrganization || !user) return false;

    try {
      const member = members.find((m) => m.id === memberId);
      if (!member) throw new Error("Member not found");

      // Prevent removing owner
      if (member.role === "owner") {
        toast.error("Cannot remove organization owner");
        return false;
      }

      const { error } = await supabase
        .from("organization_members")
        .update({ is_active: false })
        .eq("id", memberId);

      if (error) throw error;

      // Log audit event
      await supabase.from("organization_audit_log").insert({
        organization_id: currentOrganization.id,
        user_id: user.id,
        action: "remove_member",
        entity_type: "member",
        entity_id: memberId,
        before_state: { user_id: member.user_id, role: member.role },
      });

      toast.success("Member removed");
      await fetchMembersAndInvites();
      return true;
    } catch (err) {
      console.error("Error removing member:", err);
      toast.error("Failed to remove member");
      return false;
    }
  };

  const revokeInvite = async (inviteId: string): Promise<boolean> => {
    if (!currentOrganization || !user) return false;

    try {
      const { error } = await supabase
        .from("organization_invites")
        .update({ revoked_at: new Date().toISOString() })
        .eq("id", inviteId);

      if (error) throw error;

      toast.success("Invitation revoked");
      await fetchMembersAndInvites();
      return true;
    } catch (err) {
      console.error("Error revoking invite:", err);
      toast.error("Failed to revoke invitation");
      return false;
    }
  };

  const refreshOrganization = async () => {
    await fetchOrganizations();
    await fetchMembersAndInvites();
  };

  const hasPermission = (requiredRoles: OrgRole[]): boolean => {
    if (!currentRole) return false;
    return requiredRoles.includes(currentRole);
  };

  return (
    <OrganizationContext.Provider
      value={{
        organizations,
        currentOrganization,
        currentRole,
        members,
        invites,
        loading: loading || authLoading,
        error,
        switchOrganization,
        createOrganization,
        updateOrganization,
        inviteMember,
        updateMemberRole,
        removeMember,
        revokeInvite,
        refreshOrganization,
        hasPermission,
        needsOrgSetup,
      }}
    >
      {children}
    </OrganizationContext.Provider>
  );
};

export const useOrganization = () => {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error("useOrganization must be used within an OrganizationProvider");
  }
  return context;
};
